import type { ClientPerspective, SanityClient } from '@sanity/client';

import type { SanityQueryOptions, SanityQueryRunner } from './query-runner';
import { stegaFilter } from './stega-filter';

export interface SanityClientRunnerConfig {
  projectId: string;
  dataset: string;
  /** Sanity API version (date). A leading `v` is tolerated and stripped. */
  apiVersion?: string;
  /** Read token for private datasets / drafts. */
  token?: string;
  /**
   * Use the cached CDN. Defaults to `true` only when no token is set — the CDN
   * cannot serve token-authenticated (draft) reads.
   */
  useCdn?: boolean;
  /**
   * Absolute Studio URL. When set, draft reads are stega-encoded: `@sanity/client`
   * embeds zero-width characters in every string saying which document and field it
   * came from, which is what lets Visual Editing turn rendered text into a click
   * target with no per-component markup.
   *
   * Only ever applied to the draft client. A published response must stay byte-clean —
   * stega characters would leak into search indexes, analytics and copied text.
   */
  studioUrl?: string;
  /**
   * Studio workspace name; see `SanityAdapterConfig.studioWorkspace`. Defaults to
   * {@link dataset}, the same fallback the adapter applies, so a caller constructing this
   * runner directly gets a resolvable link rather than one silently missing its workspace.
   */
  studioWorkspace?: string;
}

/**
 * The Studio base URL a stega-encoded link should point at, workspace included.
 *
 * **A string, not `{baseUrl, workspace}`.** `@sanity/client` types `stega.studioUrl` as
 * `StudioUrl | ResolveStudioUrl` and `StudioUrl` admits that object — but its runtime validation
 * (`@sanity/client/src/config.ts`, the `stega.enabled` branch) accepts only a string or a
 * function and throws `stega.studioUrl must be a string or a function` on anything else. Types and
 * runtime disagree, so this compiled and then failed on the first draft request; because
 * `resolveCmsPage` turns any `getPage` throw into a silent fallback to the other CMS, the only
 * visible symptom was Presentation reporting "Unable to connect to visual editing".
 *
 * The workspace cannot simply be dropped: Eneco's Studio names a workspace per dataset and has no
 * `default`, so a bare base URL lands on "Studio workspace not found". Appending it as a path
 * segment is what `createDataAttribute` expresses with its own `workspace` option — same
 * destination, expressed the way this API accepts.
 */
export const stegaStudioUrl = (baseUrl: string, workspace?: string): string => {
  const base = baseUrl.replace(/\/+$/, '');
  const segment = workspace?.replace(/^\/+|\/+$/g, '');
  return segment ? `${base}/${segment}` : base;
};

const toClientPerspective = (perspective: NonNullable<SanityQueryOptions['perspective']>): ClientPerspective =>
  typeof perspective === 'string' ? perspective : [...perspective];

/**
 * `@sanity/client`-backed query runner.
 *
 * SERVER-ONLY: the SDK is pulled in via dynamic `import()` inside `query`, so it
 * is code-split into a chunk that is never loaded in the browser (the adapter's
 * `getPage` runs only in `getServerSideProps`). The token therefore never
 * reaches the client bundle.
 *
 * Why this supersedes the fetch runner: the SDK switches to POST for long GROQ
 * queries (no URL-length ceiling), handles retries and CDN routing, and is the
 * only transport that can stega-encode a draft response.
 *
 * Holds one client per perspective rather than one overall. They are not
 * interchangeable — the draft client is authenticated, uncached and stega-encoding,
 * the published one is none of those — and a request of either kind can arrive at
 * any time, so neither can be built by mutating the other.
 */
export const createSanityClientRunner = (config: SanityClientRunnerConfig): SanityQueryRunner => {
  const clients = new Map<'draft' | 'published', Promise<SanityClient>>();

  const getClient = (draft: boolean): Promise<SanityClient> => {
    const key = draft ? 'draft' : 'published';
    const existing = clients.get(key);
    if (existing) return existing;

    // A drafts read without a token does not fail — Sanity answers it with published
    // content and no indication that it did. That surfaces as an editor swearing their
    // unpublished change is missing, so refuse it here where the cause is still legible.
    if (draft && !config.token) {
      throw new Error(
        'Sanity draft mode requires SANITY_API_TOKEN (a Viewer token). Without it the drafts perspective silently returns published content.',
      );
    }

    const created = import('@sanity/client').then(({ createClient }) =>
      createClient({
        projectId: config.projectId,
        dataset: config.dataset,
        apiVersion: (config.apiVersion ?? '2024-01-01').replace(/^v/, ''),
        token: config.token,
        ...(draft
          ? {
              perspective: 'drafts' as const,
              // Explicit rather than leaning on the default below: `apicdn` cannot serve
              // an authenticated read, and would answer with published content instead.
              useCdn: false,
              ...(config.studioUrl
                ? {
                    stega: {
                      enabled: true,
                      studioUrl: stegaStudioUrl(config.studioUrl, config.studioWorkspace ?? config.dataset),
                      filter: stegaFilter,
                    },
                  }
                : {}),
            }
          : { useCdn: config.useCdn ?? !config.token }),
      }),
    );

    clients.set(key, created);
    return created;
  };

  return {
    query: async <T>(groq: string, params: Record<string, unknown>, options?: SanityQueryOptions): Promise<T> => {
      const perspective = options?.perspective;
      // The Studio's Drafts/Published switch changes only the perspective: the draft client stays
      // authenticated, uncached and stega-encoding, so a published preview keeps click-to-edit.
      return perspective
        ? (await getClient(true)).fetch<T>(groq, params, { perspective: toClientPerspective(perspective) })
        : (await getClient(false)).fetch<T>(groq, params);
    },
  };
};
